<script>
import { defineAsyncComponent } from 'vue';

import NextSidebar from 'next/sidebar/Sidebar.vue';
import DashboardContent from './DashboardContent.vue';

import { useUISettings } from 'dashboard/composables/useUISettings';
import { useConfig } from 'dashboard/composables/useConfig';
import { useWindowSize } from '@vueuse/core';

import wootConstants from 'dashboard/constants/globals';

import MobileSidebarLauncher from 'dashboard/components-next/sidebar/MobileSidebarLauncher.vue';

// Only a custom install ever mounts the Meridian shell, so it is never in the
// bundle a stock install downloads.
const MeridianShell = defineAsyncComponent(
  () => import('meridian/components/shell/MeridianShell.vue')
);

export default {
  components: {
    NextSidebar,
    DashboardContent,
    MeridianShell,
    MobileSidebarLauncher,
  },
  setup() {
    const { uiSettings, updateUISettings } = useUISettings();
    const { isCustom } = useConfig();
    const { width: windowWidth } = useWindowSize();

    return {
      uiSettings,
      updateUISettings,
      windowWidth,
      // The layout gate. `custom/` present at boot means the Meridian shell
      // wraps the whole app; detached, the stock layout renders untouched.
      isCustom,
    };
  },
  data() {
    return {
      showCreateAccountModal: false,
      showShortcutModal: false,
      isMobileSidebarOpen: false,
    };
  },
  computed: {
    isSmallScreen() {
      return this.windowWidth < wootConstants.SMALL_SCREEN_BREAKPOINT;
    },
    previouslyUsedDisplayType() {
      const {
        previously_used_conversation_display_type: conversationDisplayType,
      } = this.uiSettings;
      return conversationDisplayType;
    },
  },
  watch: {
    isSmallScreen: {
      handler() {
        const { LAYOUT_TYPES } = wootConstants;
        if (window.innerWidth <= wootConstants.SMALL_SCREEN_BREAKPOINT) {
          this.updateUISettings({
            conversation_display_type: LAYOUT_TYPES.EXPANDED,
          });
        } else {
          this.updateUISettings({
            conversation_display_type: this.previouslyUsedDisplayType,
          });
        }
      },
      immediate: true,
    },
  },
  methods: {
    toggleMobileSidebar() {
      this.isMobileSidebarOpen = !this.isMobileSidebarOpen;
    },
    closeMobileSidebar() {
      this.isMobileSidebarOpen = false;
    },
    openCreateAccountModal() {
      this.showCreateAccountModal = true;
    },
    closeCreateAccountModal() {
      this.showCreateAccountModal = false;
    },
    toggleKeyShortcutModal() {
      this.showShortcutModal = true;
    },
    closeKeyShortcutModal() {
      this.showShortcutModal = false;
    },
  },
};
</script>

<template>
  <MeridianShell
    v-if="isCustom"
    @show-create-account-modal="openCreateAccountModal"
    @open-key-shortcut-modal="toggleKeyShortcutModal"
  >
    <!-- The shell owns its own surface; the stock layout keeps upstream's.
         Copilot lives in the Meridian details panel, so this host must not
         mount a second one. -->
    <DashboardContent
      surface-class="bg-n-meridian-canvas"
      :hosts-copilot="false"
      :show-create-account-modal="showCreateAccountModal"
      :show-shortcut-modal="showShortcutModal"
      @close-account-create-modal="closeCreateAccountModal"
      @close-key-shortcut-modal="closeKeyShortcutModal"
    />
  </MeridianShell>

  <!-- Upstream Chatwoot's layout, unchanged: the sidebar carries its own
       header, and there is no top bar. Detached, this is all that renders. -->
  <div v-else class="flex flex-grow overflow-hidden text-n-slate-12">
    <NextSidebar
      :is-mobile-sidebar-open="isMobileSidebarOpen"
      @open-key-shortcut-modal="toggleKeyShortcutModal"
      @close-key-shortcut-modal="closeKeyShortcutModal"
      @show-create-account-modal="openCreateAccountModal"
      @close-mobile-sidebar="closeMobileSidebar"
    />
    <DashboardContent
      :show-create-account-modal="showCreateAccountModal"
      :show-shortcut-modal="showShortcutModal"
      @close-account-create-modal="closeCreateAccountModal"
      @close-key-shortcut-modal="closeKeyShortcutModal"
    >
      <template #mobile-sidebar-launcher>
        <MobileSidebarLauncher
          :is-mobile-sidebar-open="isMobileSidebarOpen"
          @toggle="toggleMobileSidebar"
        />
      </template>
    </DashboardContent>
  </div>
</template>
